import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { useLanguage } from '@/contexts/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line, AreaChart, Area } from 'recharts';
import { Download, FileText, TrendingUp, Shield, Cloud, Zap, Home, DollarSign, Calculator } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useSEO } from '@/hooks/useSEO';

const formSchema = z.object({
  // Personal Information
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(10, "Valid phone number is required"),
  company: z.string().min(2, "Company name is required"),
  
  // Company Profile
  industry: z.string().min(1, "Industry is required"),
  companySize: z.string().min(1, "Company size is required"),
  role: z.string().min(1, "Role is required"),
  
  // Digital Systems
  coreSystems: z.array(z.string()).default([]),
  systemsIntegration: z.string().min(1, "Integration status is required"),
  documentWorkflow: z.string().min(1, "Document workflow is required"),
  communicationTools: z.array(z.string()).default([]),
  
  // Automation & AI
  automatedProcesses: z.array(z.string()).default([]),
  aiUsage: z.string().min(1, "AI usage is required"),
  painPoint: z.string().min(1, "Pain point is required"),
  digitalStrategy: z.string().min(1, "Digital strategy is required"),
  techAdoption: z.string().min(1, "Tech adoption frequency is required"),
  teamOpenness: z.string().min(1, "Team openness is required"),
  
  // Infrastructure
  hosting: z.array(z.string()).default([]),
  operatingSystems: z.array(z.string()).default([]),
  containers: z.string().min(1, "Container usage is required"),
  databases: z.array(z.string()).default([]),
  backupStrategy: z.string().min(1, "Backup strategy is required"),
  
  // Security
  accessControl: z.string().min(1, "Access control is required"),
  securityTools: z.array(z.string()).default([]),
  systemUpdates: z.string().min(1, "System updates is required"),
  remoteAccess: z.string().min(1, "Remote access is required"),
  itTeam: z.string().min(1, "IT team is required"),
  
  // Goals
  priorities: z.array(z.string()).min(1, "At least one priority is required").max(3, "Maximum 3 priorities"),
  wantCall: z.string().min(1, "Call preference is required"),
});

type FormData = z.infer<typeof formSchema>;

const Assessment = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  // Set SEO for assessment page
  useSEO({
    title: language === 'en' 
      ? "🆓 FREE Digital Transformation Assessment | Get Your Score & PDF Report"
      : "🆓 Évaluation GRATUITE de Transformation Numérique | Obtenez Votre Score",
    description: language === 'en'
      ? "Take our FREE Digital Transformation Assessment! Get instant results, digital maturity score, and comprehensive PDF report. Evaluate your business processes, technology infrastructure, and AI readiness. No cost, no commitment."
      : "Faites notre Évaluation GRATUITE de Transformation Numérique ! Obtenez des résultats instantanés, score de maturité numérique et rapport PDF complet. Évaluez vos processus métier, infrastructure technologique et préparation IA.",
    keywords: language === 'en'
      ? "free digital transformation assessment, digital maturity assessment, business process evaluation, technology readiness assessment, AI readiness evaluation, free assessment report, digital transformation score, business digitization assessment"
      : "évaluation gratuite transformation numérique, évaluation maturité numérique, évaluation processus métier, évaluation préparation technologique, évaluation préparation IA, rapport évaluation gratuit",
    canonical: "https://quantumbox.ai/assessment"
  });
  const [currentSection, setCurrentSection] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [scores, setScores] = useState<any>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      industry: '',
      companySize: '',
      role: '',
      coreSystems: [],
      systemsIntegration: '',
      documentWorkflow: '',
      communicationTools: [],
      automatedProcesses: [],
      aiUsage: '',
      painPoint: '',
      digitalStrategy: '',
      techAdoption: '',
      teamOpenness: '',
      hosting: [],
      operatingSystems: [],
      containers: '',
      databases: [],
      backupStrategy: '',
      accessControl: '',
      securityTools: [],
      systemUpdates: '',
      remoteAccess: '',
      itTeam: '',
      priorities: [],
      wantCall: '',
    },
  });

  const sections = [
    { title: language === 'en' ? 'Personal Information' : 'Informations Personnelles', icon: FileText },
    { title: language === 'en' ? 'Company Profile' : 'Profil de l\'Entreprise', icon: TrendingUp },
    { title: language === 'en' ? 'Digital Systems' : 'Systèmes Numériques', icon: Cloud },
    { title: language === 'en' ? 'Automation & AI' : 'Automatisation et IA', icon: Zap },
    { title: language === 'en' ? 'Infrastructure' : 'Infrastructure', icon: Cloud },
    { title: language === 'en' ? 'Security' : 'Sécurité', icon: Shield },
    { title: language === 'en' ? 'Business Goals' : 'Objectifs', icon: TrendingUp },
  ];

  const calculateScores = (data: FormData) => {
    let workflowScore = 0;
    let infrastructureScore = 0;
    let securityScore = 0;

    // Workflow scoring (0-100)
    // Core systems scoring (0-20 points)
    const coreSystemsValue = data.coreSystems.length * 3;
    workflowScore += Math.min(coreSystemsValue, 20);
    
    // Integration scoring (0-20 points)
    if (data.systemsIntegration === 'fully') workflowScore += 20;
    else if (data.systemsIntegration === 'partially') workflowScore += 12;
    else if (data.systemsIntegration === 'no') workflowScore += 5;
    
    // Document workflow (0-15 points)
    if (data.documentWorkflow === 'fully-digital') workflowScore += 15;
    else if (data.documentWorkflow === 'partly-digital') workflowScore += 10;
    else if (data.documentWorkflow === 'manual') workflowScore += 3;
    
    // AI usage (0-25 points)
    if (data.aiUsage === 'yes-actively') workflowScore += 25;
    else if (data.aiUsage === 'exploring') workflowScore += 15;
    else if (data.aiUsage === 'interested') workflowScore += 8;
    else workflowScore += 0;
    
    // Digital strategy (0-20 points)
    if (data.digitalStrategy === 'fully-developed') workflowScore += 20;
    else if (data.digitalStrategy === 'in-progress') workflowScore += 12;
    else if (data.digitalStrategy === 'planned') workflowScore += 6;
    else workflowScore += 0;
    
    // Infrastructure scoring (0-100)
    // Hosting diversity (0-25 points)
    const hostingValue = data.hosting.length * 8;
    infrastructureScore += Math.min(hostingValue, 25);
    
    // Container usage (0-20 points)
    if (data.containers === 'yes') infrastructureScore += 20;
    else if (data.containers === 'planning') infrastructureScore += 10;
    else infrastructureScore += 0;
    
    // Database variety (0-20 points)
    const dbValue = data.databases.length * 7;
    infrastructureScore += Math.min(dbValue, 20);
    
    // Backup strategy (0-25 points)
    if (data.backupStrategy === 'cloud-automated') infrastructureScore += 25;
    else if (data.backupStrategy === 'local-offline') infrastructureScore += 15;
    else if (data.backupStrategy === 'no-strategy') infrastructureScore += 0;
    
    // OS variety (0-10 points)
    const osValue = data.operatingSystems.length * 3;
    infrastructureScore += Math.min(osValue, 10);
    
    // Security scoring (0-100)
    // Access control (0-25 points)
    if (data.accessControl === 'strict') securityScore += 25;
    else if (data.accessControl === 'partially') securityScore += 15;
    else if (data.accessControl === 'no') securityScore += 0;
    
    // Security tools (0-30 points)
    const securityToolsValue = data.securityTools.length * 6;
    securityScore += Math.min(securityToolsValue, 30);
    
    // System updates (0-20 points)
    if (data.systemUpdates === 'automatic') securityScore += 20;
    else if (data.systemUpdates === 'occasionally') securityScore += 10;
    else if (data.systemUpdates === 'rarely') securityScore += 3;
    
    // Remote access (0-15 points)
    if (data.remoteAccess === 'yes') securityScore += 15;
    else if (data.remoteAccess === 'no') securityScore += 5;
    
    // IT team (0-10 points)
    if (data.itTeam === 'in-house') securityScore += 10;
    else if (data.itTeam === 'partially-outsourced') securityScore += 7;
    else if (data.itTeam === 'no-adhoc') securityScore += 2;

    // Cap scores at 100
    workflowScore = Math.min(Math.round(workflowScore), 100);
    infrastructureScore = Math.min(Math.round(infrastructureScore), 100);
    securityScore = Math.min(Math.round(securityScore), 100);

    const totalScore = Math.round((workflowScore + infrastructureScore + securityScore) / 3);

    return {
      total: totalScore,
      workflow: workflowScore,
      infrastructure: infrastructureScore,
      security: securityScore,
      maturityLevel: totalScore <= 40 ? 'starter' : totalScore <= 70 ? 'emerging' : 'advanced',
    };
  };

  const calculateInvestmentAndROI = (data: FormData, scores: any) => {
    // Company size multipliers
    const sizeMultipliers = {
      '1-10': { base: 15000, multiplier: 1.0 },
      '11-50': { base: 45000, multiplier: 1.8 },
      '51-200': { base: 125000, multiplier: 2.5 },
      '201-1000': { base: 350000, multiplier: 3.2 },
      '1000+': { base: 750000, multiplier: 4.0 }
    };

    // Maturity level adjustments
    const maturityAdjustments = {
      starter: 1.5, // Higher investment needed for basic setup
      emerging: 1.2, // Moderate investment for improvements
      advanced: 0.8  // Lower investment, focus on optimization
    };

    const sizeConfig = sizeMultipliers[data.companySize as keyof typeof sizeMultipliers] || sizeMultipliers['11-50'];
    const maturityMultiplier = maturityAdjustments[scores.maturityLevel as keyof typeof maturityAdjustments];
    
    // Base investment calculation
    const baseInvestment = sizeConfig.base * sizeConfig.multiplier * maturityMultiplier;
    
    // ROI calculation based on efficiency gains
    const efficiencyGains = {
      starter: 0.25, // 25% efficiency improvement
      emerging: 0.35, // 35% efficiency improvement
      advanced: 0.45  // 45% efficiency improvement
    };

    const annualSavingsRate = efficiencyGains[scores.maturityLevel as keyof typeof efficiencyGains];
    
    // Estimate annual operational costs based on company size
    const operationalCosts = {
      '1-10': 200000,
      '11-50': 800000,
      '51-200': 3000000,
      '201-1000': 12000000,
      '1000+': 50000000
    };

    const annualOps = operationalCosts[data.companySize as keyof typeof operationalCosts] || 800000;
    const annualSavings = annualOps * annualSavingsRate;
    
    // 3-year projection
    const projectionData = [
      {
        year: language === 'en' ? 'Year 0' : 'Année 0',
        investment: baseInvestment,
        savings: 0,
        cumulative: -baseInvestment,
        roi: -100
      },
      {
        year: language === 'en' ? 'Year 1' : 'Année 1',
        investment: baseInvestment * 0.2, // 20% additional in year 1
        savings: annualSavings * 0.6, // 60% of full savings in year 1
        cumulative: -baseInvestment + (annualSavings * 0.6) - (baseInvestment * 0.2),
        roi: (((annualSavings * 0.6) - (baseInvestment * 0.2)) / baseInvestment) * 100
      },
      {
        year: language === 'en' ? 'Year 2' : 'Année 2',
        investment: baseInvestment * 0.1, // 10% maintenance
        savings: annualSavings,
        cumulative: -baseInvestment + (annualSavings * 1.6) - (baseInvestment * 0.3),
        roi: (((annualSavings * 1.6) - (baseInvestment * 0.3)) / baseInvestment) * 100
      },
      {
        year: language === 'en' ? 'Year 3' : 'Année 3',
        investment: baseInvestment * 0.1,
        savings: annualSavings * 1.1, // 10% growth in savings
        cumulative: -baseInvestment + (annualSavings * 2.7) - (baseInvestment * 0.4),
        roi: (((annualSavings * 2.7) - (baseInvestment * 0.4)) / baseInvestment) * 100
      }
    ];

    return {
      totalInvestment: Math.round(baseInvestment),
      annualSavings: Math.round(annualSavings),
      breakEvenPoint: Math.round((baseInvestment / annualSavings) * 12), // in months
      threeYearROI: Math.round((((annualSavings * 2.7) - (baseInvestment * 0.4)) / baseInvestment) * 100),
      projectionData
    };
  };

  const getMaturityLevel = () => {
    if (!scores) return { title: '', description: '', color: '#9ca3af', bgColor: '#f3f4f6' };
    
    const total = scores.total;
    
    if (total >= 80) {
      return {
        title: language === 'en' ? 'Excellence Level' : 'Niveau Excellence',
        description: language === 'en' 
          ? 'Outstanding digital maturity — leading edge implementation.' 
          : 'Maturité numérique exceptionnelle — implémentation de pointe.',
        color: '#059669', // green-600
        bgColor: '#d1fae5' // green-100
      };
    } else if (total >= 60) {
      return {
        title: language === 'en' ? 'Advanced Level' : 'Niveau Avancé',
        description: language === 'en'
          ? 'Strong digital foundation — ready for AI optimization.'
          : 'Base numérique solide — prêt pour l\'optimisation IA.',
        color: '#0d9488', // teal-600
        bgColor: '#ccfbf1' // teal-100
      };
    } else if (total >= 40) {
      return {
        title: language === 'en' ? 'Emerging Level' : 'Niveau Émergent',
        description: language === 'en'
          ? 'Good progress — time to scale and secure systems.'
          : 'Bon progrès — il est temps d\'évoluer et sécuriser.',
        color: '#d97706', // amber-600
        bgColor: '#fef3c7' // amber-100
      };
    } else if (total >= 20) {
      return {
        title: language === 'en' ? 'Starter Level' : 'Niveau Débutant',
        description: language === 'en' 
          ? 'High opportunity — beginning of digital transformation.' 
          : 'Grande opportunité — début de la transformation numérique.',
        color: '#dc2626', // red-600
        bgColor: '#fee2e2' // red-100
      };
    } else {
      return {
        title: language === 'en' ? 'Early Stage' : 'Stade Précoce',
        description: language === 'en' 
          ? 'Significant potential — foundational changes needed.' 
          : 'Potentiel important — changements fondamentaux nécessaires.',
        color: '#4b5563', // gray-600
        bgColor: '#f3f4f6' // gray-100
      };
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      // Save to Supabase
      const { error } = await supabase
        .from('assessments')
        .insert({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company,
          industry: data.industry,
          company_size: data.companySize,
          role: data.role,
          core_systems: data.coreSystems,
          systems_integration: data.systemsIntegration,
          document_workflow: data.documentWorkflow,
          communication_tools: data.communicationTools,
          automated_processes: data.automatedProcesses,
          ai_usage: data.aiUsage,
          pain_point: data.painPoint,
          digital_strategy: data.digitalStrategy,
          tech_adoption: data.techAdoption,
          team_openness: data.teamOpenness,
          hosting: data.hosting,
          operating_systems: data.operatingSystems,
          containers: data.containers,
          databases: data.databases,
          backup_strategy: data.backupStrategy,
          monitoring: 'not_specified', // Default value since not in form
          disaster_recovery: 'not_specified', // Default value since not in form
          security_tools: data.securityTools,
          security_concerns: 'not_specified', // Default value since not in form
          compliance_requirements: 'not_specified', // Default value since not in form
          access_control: data.accessControl,
          priorities: data.priorities,
          timeline: 'not_specified', // Default value since not in form
          budget: 'not_specified', // Default value since not in form
          success_metrics: 'not_specified', // Default value since not in form
          consultation_interest: data.wantCall,
        });

      if (error) throw error;

      const { notifyFormSubmission } = await import('@/lib/notifyForm');
      notifyFormSubmission('Digital Maturity Assessment', {
        name: data.name, email: data.email, phone: data.phone,
        company: data.company, industry: data.industry, role: data.role,
        consultation_interest: data.wantCall,
      });

      const calculatedScores = calculateScores(data);
      const roiData = calculateInvestmentAndROI(data, calculatedScores);
      setScores({ ...calculatedScores, roi: roiData });
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      // Still show results even if save fails
      const calculatedScores = calculateScores(data);
      const roiData = calculateInvestmentAndROI(data, calculatedScores);
      setScores({ ...calculatedScores, roi: roiData });
      setShowResults(true);
    }
  };

  const nextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const generatePDF = async () => {
    try {
      const formData = form.getValues();
      const companyName = formData.company || 'Your Organization';
      
      // Multi-language text content
      const pdfTexts = {
        en: {
          title: 'Digital Transformation',
          subtitle: 'Assessment Report',
          generatedOn: 'Generated on',
          executiveSummary: 'Executive Summary',
          keyFindings: 'Key Findings',
          investmentOverview: 'Investment Overview',
          detailedScores: 'Detailed Assessment Scores',
          strategicRecommendations: 'Strategic Recommendations',
          nextSteps: 'Next Steps & Contact Information',
          actionPlan: 'Recommended Action Plan',
          expertGuidance: 'Get Expert Guidance',
          contactDescription: 'Schedule a free consultation to discuss your digital transformation strategy and implementation roadmap.',
          confidential: 'Confidential & Proprietary',
          overallMaturity: 'Overall Digital Maturity:',
          workflowEfficiency: 'Workflow Efficiency:',
          infrastructureReadiness: 'Infrastructure Readiness:',
          securityPosture: 'Security Posture:',
          estimatedROI: 'Estimated ROI (3 years):',
          recommendedInvestment: 'Recommended Investment:',
          annualSavings: 'Estimated Annual Gains:',
          breakEven: 'Break-even Point:',
          months: 'months',
          workflowProcesses: 'Workflow & Processes',
          infrastructureTech: 'Infrastructure & Technology',
          securityCompliance: 'Security & Compliance',
          actionSteps: [
            '1. Review this assessment with your leadership team',
            '2. Prioritize recommendations based on business impact',
            '3. Develop a digital transformation roadmap',
            '4. Schedule a consultation with our experts',
            '5. Begin implementation with quick wins'
          ]
        },
        fr: {
          title: 'Transformation Numérique',
          subtitle: 'Rapport d\'Évaluation',
          generatedOn: 'Généré le',
          executiveSummary: 'Résumé Exécutif',
          keyFindings: 'Principales Conclusions',
          investmentOverview: 'Aperçu des Investissements',
          detailedScores: 'Scores d\'Évaluation Détaillés',
          strategicRecommendations: 'Recommandations Stratégiques',
          nextSteps: 'Prochaines Étapes & Informations de Contact',
          actionPlan: 'Plan d\'Action Recommandé',
          expertGuidance: 'Obtenez des Conseils d\'Expert',
          contactDescription: 'Planifiez une consultation gratuite pour discuter de votre stratégie de transformation numérique et feuille de route d\'implémentation.',
          confidential: 'Confidentiel et Propriétaire',
          overallMaturity: 'Maturité numérique globale:',
          workflowEfficiency: 'Efficacité des processus:',
          infrastructureReadiness: 'Préparation infrastructure:',
          securityPosture: 'Posture sécuritaire:',
          estimatedROI: 'ROI estimé (3 ans):',
          recommendedInvestment: 'Investissement recommandé:',
          annualSavings: 'Économies annuelles potentielles:',
          breakEven: 'Point d\'équilibre:',
          months: 'mois',
          workflowProcesses: 'Processus & Flux de travail',
          infrastructureTech: 'Infrastructure & Technologie',
          securityCompliance: 'Sécurité & Conformité',
          actionSteps: [
            '1. Examiner cette évaluation avec votre équipe de direction',
            '2. Prioriser les recommandations selon l\'impact commercial',
            '3. Développer une feuille de route de transformation numérique',
            '4. Planifier une consultation avec nos experts',
            '5. Commencer l\'implémentation avec des gains rapides'
          ]
        },
        es: {
          title: 'Transformación Digital',
          subtitle: 'Informe de Evaluación',
          generatedOn: 'Generado el',
          executiveSummary: 'Resumen Ejecutivo',
          keyFindings: 'Hallazgos Clave',
          investmentOverview: 'Resumen de Inversión',
          detailedScores: 'Puntuaciones de Evaluación Detalladas',
          strategicRecommendations: 'Recomendaciones Estratégicas',
          nextSteps: 'Próximos Pasos e Información de Contacto',
          actionPlan: 'Plan de Acción Recomendado',
          expertGuidance: 'Obtener Orientación Experta',
          contactDescription: 'Programar una consulta gratuita para discutir su estrategia de transformación digital y hoja de ruta de implementación.',
          confidential: 'Confidencial y Propietario',
          overallMaturity: 'Madurez Digital General:',
          workflowEfficiency: 'Eficiencia de Procesos:',
          infrastructureReadiness: 'Preparación de Infraestructura:',
          securityPosture: 'Postura de Seguridad:',
          estimatedROI: 'ROI Estimado (3 años):',
          recommendedInvestment: 'Inversión Recomendada:',
          annualSavings: 'Potencial de Ahorro Anual:',
          breakEven: 'Punto de Equilibrio:',
          months: 'meses',
          workflowProcesses: 'Flujos de Trabajo y Procesos',
          infrastructureTech: 'Infraestructura y Tecnología',
          securityCompliance: 'Seguridad y Cumplimiento',
          actionSteps: [
            '1. Revisar esta evaluación con su equipo directivo',
            '2. Priorizar recomendaciones basadas en impacto comercial',
            '3. Desarrollar una hoja de ruta de transformación digital',
            '4. Programar una consulta con nuestros expertos',
            '5. Comenzar implementación con victorias rápidas'
          ]
        },
        pt: {
          title: 'Transformação Digital',
          subtitle: 'Relatório de Avaliação',
          generatedOn: 'Gerado em',
          executiveSummary: 'Resumo Executivo',
          keyFindings: 'Principais Descobertas',
          investmentOverview: 'Visão Geral do Investimento',
          detailedScores: 'Pontuações de Avaliação Detalhadas',
          strategicRecommendations: 'Recomendações Estratégicas',
          nextSteps: 'Próximos Passos e Informações de Contato',
          actionPlan: 'Plano de Ação Recomendado',
          expertGuidance: 'Obter Orientação Especializada',
          contactDescription: 'Agendar uma consulta gratuita para discutir sua estratégia de transformação digital e roteiro de implementação.',
          confidential: 'Confidencial e Proprietário',
          overallMaturity: 'Maturidade Digital Geral:',
          workflowEfficiency: 'Eficiência dos Processos:',
          infrastructureReadiness: 'Prontidão da Infraestrutura:',
          securityPosture: 'Postura de Segurança:',
          estimatedROI: 'ROI Estimado (3 anos):',
          recommendedInvestment: 'Investimento Recomendado:',
          annualSavings: 'Potencial de Economia Anual:',
          breakEven: 'Ponto de Equilíbrio:',
          months: 'meses',
          workflowProcesses: 'Fluxos de Trabalho e Processos',
          infrastructureTech: 'Infraestrutura e Tecnologia',
          securityCompliance: 'Segurança e Conformidade',
          actionSteps: [
            '1. Revisar esta avaliação com sua equipe de liderança',
            '2. Priorizar recomendações baseadas no impacto nos negócios',
            '3. Desenvolver um roteiro de transformação digital',
            '4. Agendar uma consulta com nossos especialistas',
            '5. Começar implementação com vitórias rápidas'
          ]
        }
      };

      const texts = pdfTexts[language as keyof typeof pdfTexts] || pdfTexts.en;
      
      // Show loading state
      const loadingToast = document.createElement('div');
      loadingToast.textContent = language === 'en' ? 'Generating Professional PDF Report...' : 
                                  language === 'fr' ? 'Génération du Rapport PDF Professionnel...' :
                                  language === 'es' ? 'Generando Informe PDF Profesional...' :
                                  'Gerando Relatório PDF Profissional...';
      loadingToast.style.cssText = 'position:fixed;top:20px;right:20px;background:linear-gradient(135deg, #3b82f6, #8b5cf6);color:white;padding:15px 20px;border-radius:10px;z-index:9999;box-shadow:0 10px 30px rgba(0,0,0,0.3);font-weight:600;';
      document.body.appendChild(loadingToast);

      // Create PDF with professional styling
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pdfWidth - (2 * margin);
      let currentY = margin;
      
      // Professional header design
      const addProfessionalHeader = (pageNum: number, totalPages: number, includeTitle: boolean = true) => {
        // Header background
        pdf.setFillColor(59, 130, 246);
        pdf.rect(0, 0, pdfWidth, 25, 'F');
        
        // Company logo
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('QB', margin, 18);
        
        if (includeTitle) {
          // Title
          pdf.setFontSize(12);
          pdf.text(texts.title, margin + 15, 18);
        }
        
        // Page number and date (right)
        pdf.setFontSize(10);
        const pageText = language === 'en' ? 'Page' : 
                        language === 'fr' ? 'Page' :
                        language === 'es' ? 'Página' : 'Página';
        pdf.text(`${new Date().toLocaleDateString()} | ${pageText} ${pageNum}/${totalPages}`, pdfWidth - margin, 18, { align: 'right' });
        
        return 35; // Return Y position after header
      };

      const addProfessionalFooter = () => {
        // Footer line
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.5);
        pdf.line(margin, pdfHeight - 20, pdfWidth - margin, pdfHeight - 20);
        
        // Company info and confidential notice
        pdf.setTextColor(120, 120, 120);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        const companyText = language === 'en' ? 'QuantumBox.ai - Digital Transformation Consulting' :
                           language === 'fr' ? 'QuantumBox.ai - Conseil en Transformation Numérique' :
                           language === 'es' ? 'QuantumBox.ai - Consultoría en Transformación Digital' :
                           'QuantumBox.ai - Consultoria em Transformação Digital';
        pdf.text(companyText, margin, pdfHeight - 10);
        pdf.text(texts.confidential, pdfWidth - margin, pdfHeight - 10, { align: 'right' });
      };

      const addPageBreak = () => {
        pdf.addPage();
        currentY = addProfessionalHeader(pdf.getNumberOfPages(), 0, true);
      };

      const checkPageSpace = (spaceNeeded: number) => {
        if (currentY + spaceNeeded > pdfHeight - 40) {
          addPageBreak();
        }
      };

      // COVER PAGE
      currentY = addProfessionalHeader(1, 0, false);
      
      // Cover page gradient background
      for (let i = 0; i < 100; i++) {
        const alpha = (100 - i) / 100;
        pdf.setFillColor(59, 130, 246, alpha * 0.05);
        pdf.rect(0, 25 + i * 2, pdfWidth, 2, 'F');
      }
      
      // Main title section
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(32);
      pdf.setFont('helvetica', 'bold');
      pdf.text(texts.title, pdfWidth / 2, 80, { align: 'center' });
      
      pdf.setFontSize(24);
      pdf.text(texts.subtitle, pdfWidth / 2, 95, { align: 'center' });
      
      // Company info box
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(margin, 110, contentWidth, 40, 5, 5, 'F');
      pdf.setDrawColor(229, 231, 235);
      pdf.roundedRect(margin, 110, contentWidth, 40, 5, 5);
      
      pdf.setFontSize(18);
      pdf.setTextColor(59, 130, 246);
      pdf.setFont('helvetica', 'bold');
      pdf.text(companyName, pdfWidth / 2, 125, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.setTextColor(107, 114, 128);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${formData.industry || 'Industry'} | ${formData.companySize || 'Company Size'}`, pdfWidth / 2, 135, { align: 'center' });
      pdf.text(`${texts.generatedOn} ${new Date().toLocaleDateString()}`, pdfWidth / 2, 145, { align: 'center' });
      
      // Score visualization
      const score = scores.total;
      const scoreColor = score >= 80 ? [34, 197, 94] : score >= 60 ? [168, 85, 247] : score >= 40 ? [251, 146, 60] : [239, 68, 68];
      
      // Score circle
      pdf.setFillColor(scoreColor[0], scoreColor[1], scoreColor[2], 0.1);
      pdf.circle(pdfWidth / 2, 190, 30, 'F');
      pdf.setDrawColor(scoreColor[0], scoreColor[1], scoreColor[2]);
      pdf.setLineWidth(4);
      pdf.circle(pdfWidth / 2, 190, 30);
      
      pdf.setTextColor(scoreColor[0], scoreColor[1], scoreColor[2]);
      pdf.setFontSize(42);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${score}`, pdfWidth / 2, 200, { align: 'center' });
      
      // Maturity level
      const maturityInfo = getMaturityLevel();
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(maturityInfo.title, pdfWidth / 2, 235, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.setTextColor(107, 114, 128);
      pdf.setFont('helvetica', 'normal');
      const wrappedDescription = pdf.splitTextToSize(maturityInfo.description, contentWidth - 40);
      pdf.text(wrappedDescription, pdfWidth / 2, 245, { align: 'center' });

      addProfessionalFooter();

      // EXECUTIVE SUMMARY PAGE
      addPageBreak();
      
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(texts.executiveSummary, margin, currentY);
      currentY += 15;
      
      // Key findings box
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(margin, currentY, contentWidth, 60, 3, 3, 'F');
      pdf.setDrawColor(59, 130, 246);
      pdf.setLineWidth(1);
      pdf.roundedRect(margin, currentY, contentWidth, 60, 3, 3);
      
      pdf.setFontSize(14);
      pdf.setTextColor(59, 130, 246);
      pdf.setFont('helvetica', 'bold');
      pdf.text(texts.keyFindings, margin + 10, currentY + 15);
      
      pdf.setFontSize(11);
      pdf.setTextColor(31, 41, 55);
      pdf.setFont('helvetica', 'normal');
      
      const findings = [
        `${texts.overallMaturity} ${score}% (${maturityInfo.title})`,
        `${texts.workflowEfficiency} ${scores.workflow}%`,
        `${texts.infrastructureReadiness} ${scores.infrastructure}%`,
        `${texts.securityPosture} ${scores.security}%`,
        `${texts.estimatedROI} ${scores.roi?.threeYearROI || 0}%`
      ];
      
      findings.forEach((finding, index) => {
        pdf.text(`• ${finding}`, margin + 15, currentY + 25 + (index * 7));
      });
      
      currentY += 75;
      
      // Investment overview
      checkPageSpace(50);
      pdf.setFillColor(254, 249, 195);
      pdf.roundedRect(margin, currentY, contentWidth, 45, 3, 3, 'F');
      pdf.setDrawColor(251, 191, 36);
      pdf.roundedRect(margin, currentY, contentWidth, 45, 3, 3);
      
      pdf.setFontSize(14);
      pdf.setTextColor(251, 191, 36);
      pdf.setFont('helvetica', 'bold');
      pdf.text(texts.investmentOverview, margin + 10, currentY + 15);
      
      pdf.setFontSize(11);
      pdf.setTextColor(31, 41, 55);
      pdf.setFont('helvetica', 'normal');
      
      const investmentText = [
        `${texts.recommendedInvestment} $${scores.roi?.totalInvestment?.toLocaleString() || 'N/A'}`,
        `${texts.annualSavings} $${scores.roi?.annualSavings?.toLocaleString() || 'N/A'}`,
        `${texts.breakEven} ${scores.roi?.breakEvenPoint || 0} ${texts.months}`
      ];
      
      investmentText.forEach((text, index) => {
        pdf.text(`• ${text}`, margin + 15, currentY + 25 + (index * 7));
      });
      
      currentY += 60;

      // DETAILED SCORES PAGE
      addPageBreak();
      
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(texts.detailedScores, margin, currentY);
      currentY += 20;
      
      // Score breakdown
      const scoreCategories = [
        { name: texts.workflowProcesses, score: scores.workflow, color: [59, 130, 246] },
        { name: texts.infrastructureTech, score: scores.infrastructure, color: [168, 85, 247] },
        { name: texts.securityCompliance, score: scores.security, color: [34, 197, 94] }
      ];
      
      scoreCategories.forEach((category, index) => {
        checkPageSpace(25);
        
        // Category header
        pdf.setFontSize(14);
        pdf.setTextColor(category.color[0], category.color[1], category.color[2]);
        pdf.setFont('helvetica', 'bold');
        pdf.text(category.name, margin, currentY);
        
        // Score bar
        const barWidth = contentWidth - 60;
        const scoreWidth = (category.score / 100) * barWidth;
        
        // Background bar
        pdf.setFillColor(243, 244, 246);
        pdf.roundedRect(margin, currentY + 5, barWidth, 8, 2, 2, 'F');
        
        // Score bar
        pdf.setFillColor(category.color[0], category.color[1], category.color[2]);
        pdf.roundedRect(margin, currentY + 5, scoreWidth, 8, 2, 2, 'F');
        
        // Score text
        pdf.setTextColor(31, 41, 55);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${category.score}%`, margin + barWidth + 10, currentY + 12);
        
        currentY += 30;
      });

      // RECOMMENDATIONS PAGE
      addPageBreak();
      
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(texts.strategicRecommendations, margin, currentY);
      currentY += 20;
      
      // Priority recommendations based on maturity level
      const getRecommendations = () => {
        if (scores.total >= 80) {
          return language === 'en' ? [
            'Implement advanced AI and machine learning solutions',
            'Establish innovation labs for emerging technologies',
            'Develop strategic partnerships for technology advancement',
            'Create comprehensive data analytics and business intelligence platforms'
          ] : language === 'fr' ? [
            'Implémenter des solutions IA et d\'apprentissage automatique avancées',
            'Établir des laboratoires d\'innovation pour les technologies émergentes',
            'Développer des partenariats stratégiques pour l\'avancement technologique',
            'Créer des plateformes complètes d\'analyse de données et d\'intelligence d\'affaires'
          ] : language === 'es' ? [
            'Implementar soluciones avanzadas de IA y aprendizaje automático',
            'Establecer laboratorios de innovación para tecnologías emergentes',
            'Desarrollar alianzas estratégicas para el avance tecnológico',
            'Crear plataformas integrales de análisis de datos e inteligencia empresarial'
          ] : [
            'Implementar soluções avançadas de IA e aprendizado de máquina',
            'Estabelecer laboratórios de inovação para tecnologias emergentes',
            'Desenvolver parcerias estratégicas para avanço tecnológico',
            'Criar plataformas abrangentes de análise de dados e inteligência empresarial'
          ];
        } else if (scores.total >= 60) {
          return language === 'en' ? [
            'Optimize existing systems integration and automation',
            'Enhance cybersecurity measures and compliance frameworks',
            'Implement advanced analytics and reporting capabilities',
            'Develop comprehensive staff training programs for digital tools'
          ] : language === 'fr' ? [
            'Optimiser l\'intégration et l\'automatisation des systèmes existants',
            'Renforcer les mesures de cybersécurité et les cadres de conformité',
            'Implémenter des capacités d\'analyse et de rapport avancées',
            'Développer des programmes de formation complets pour les outils numériques'
          ] : language === 'es' ? [
            'Optimizar la integración de sistemas existentes y automatización',
            'Mejorar las medidas de ciberseguridad y marcos de cumplimiento',
            'Implementar capacidades avanzadas de análisis e informes',
            'Desarrollar programas integrales de capacitación para herramientas digitales'
          ] : [
            'Otimizar a integração e automação de sistemas existentes',
            'Aprimorar medidas de cibersegurança e estruturas de conformidade',
            'Implementar capacidades avançadas de análise e relatórios',
            'Desenvolver programas abrangentes de treinamento para ferramentas digitais'
          ];
        } else if (scores.total >= 40) {
          return language === 'en' ? [
            'Establish core digital infrastructure and cloud migration strategy',
            'Implement basic automation for repetitive tasks',
            'Develop data management and backup strategies',
            'Create digital workflow documentation and standardization'
          ] : language === 'fr' ? [
            'Établir une infrastructure numérique de base et une stratégie de migration cloud',
            'Implémenter une automatisation de base pour les tâches répétitives',
            'Développer des stratégies de gestion et de sauvegarde des données',
            'Créer une documentation et une standardisation des flux de travail numériques'
          ] : language === 'es' ? [
            'Establecer infraestructura digital central y estrategia de migración a la nube',
            'Implementar automatización básica para tareas repetitivas',
            'Desarrollar estrategias de gestión de datos y respaldo',
            'Crear documentación y estandarización de flujos de trabajo digitales'
          ] : [
            'Estabelecer infraestrutura digital central e estratégia de migração para nuvem',
            'Implementar automação básica para tarefas repetitivas',
            'Desenvolver estratégias de gerenciamento e backup de dados',
            'Criar documentação e padronização de fluxos de trabalho digitais'
          ];
        } else {
          return language === 'en' ? [
            'Begin digital transformation with basic system implementations',
            'Establish fundamental cybersecurity protocols',
            'Migrate from manual to digital processes gradually',
            'Invest in staff training and change management'
          ] : language === 'fr' ? [
            'Commencer la transformation numérique avec des implémentations système de base',
            'Établir des protocoles de cybersécurité fondamentaux',
            'Migrer progressivement des processus manuels vers numériques',
            'Investir dans la formation du personnel et la gestion du changement'
          ] : language === 'es' ? [
            'Comenzar la transformación digital con implementaciones básicas de sistemas',
            'Establecer protocolos fundamentales de ciberseguridad',
            'Migrar gradualmente de procesos manuales a digitales',
            'Invertir en capacitación del personal y gestión del cambio'
          ] : [
            'Começar a transformação digital com implementações básicas de sistemas',
            'Estabelecer protocolos fundamentais de cibersegurança',
            'Migrar gradualmente de processos manuais para digitais',
            'Investir em treinamento de pessoal e gestão de mudanças'
          ];
        }
      };
      
      const recommendations = getRecommendations();
      
      recommendations.forEach((rec, index) => {
        checkPageSpace(15);
        
        // Priority indicator
        pdf.setFillColor(59, 130, 246);
        pdf.circle(margin + 5, currentY + 3, 3, 'F');
        
        // Recommendation text
        pdf.setFontSize(11);
        pdf.setTextColor(31, 41, 55);
        pdf.setFont('helvetica', 'normal');
        const wrappedText = pdf.splitTextToSize(rec, contentWidth - 20);
        pdf.text(wrappedText, margin + 15, currentY + 5);
        
        currentY += (wrappedText.length * 5) + 10;
      });

      // NEXT STEPS PAGE
      addPageBreak();
      
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(texts.nextSteps, margin, currentY);
      currentY += 20;
      
      // Action plan
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(margin, currentY, contentWidth, 80, 3, 3, 'F');
      pdf.setDrawColor(59, 130, 246);
      pdf.roundedRect(margin, currentY, contentWidth, 80, 3, 3);
      
      pdf.setFontSize(14);
      pdf.setTextColor(59, 130, 246);
      pdf.setFont('helvetica', 'bold');
      pdf.text(texts.actionPlan, margin + 10, currentY + 15);
      
      pdf.setFontSize(10);
      pdf.setTextColor(31, 41, 55);
      pdf.setFont('helvetica', 'normal');
      
      texts.actionSteps.forEach((step, index) => {
        pdf.text(step, margin + 15, currentY + 30 + (index * 10));
      });
      
      currentY += 100;
      
      // Contact information
      checkPageSpace(60);
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(margin, currentY, contentWidth, 50, 3, 3, 'F');
      
      pdf.setFontSize(14);
      pdf.setTextColor(31, 41, 55);
      pdf.setFont('helvetica', 'bold');
      pdf.text(texts.expertGuidance, margin + 10, currentY + 15);
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const wrappedContact = pdf.splitTextToSize(texts.contactDescription, contentWidth - 20);
      pdf.text(wrappedContact, margin + 10, currentY + 25);
      
      pdf.setTextColor(59, 130, 246);
      pdf.setFont('helvetica', 'bold');
      pdf.text('📧 contact@quantumbox.ai | 🌐 quantumbox.ai', margin + 10, currentY + 40);

      // Update page numbers
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        if (i > 1) {
          // Update header with correct page numbers
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(10);
          const pageText = language === 'en' ? 'Page' : 
                          language === 'fr' ? 'Page' :
                          language === 'es' ? 'Página' : 'Página';
          pdf.text(`${new Date().toLocaleDateString()} | ${pageText} ${i}/${totalPages}`, pdfWidth - margin, 18, { align: 'right' });
        }
      }

      addProfessionalFooter();

      // Generate professional filename
      const companyPart = companyName.replace(/[^a-zA-Z0-9]/g, '-');
      const datePart = new Date().toISOString().split('T')[0];
      const langSuffix = language === 'en' ? '' : `-${language.toUpperCase()}`;
      const fileName = `${companyPart}-Digital-Transformation-Assessment${langSuffix}-${datePart}.pdf`;
      
      // Save PDF
      pdf.save(fileName);
      
      // Remove loading toast
      document.body.removeChild(loadingToast);
      
      // Success message
      const successToast = document.createElement('div');
      successToast.textContent = language === 'en' ? '✓ Professional PDF Generated Successfully!' : '✓ PDF Professionnel Généré avec Succès!';
      successToast.style.cssText = 'position:fixed;top:20px;right:20px;background:linear-gradient(135deg, #10b981, #059669);color:white;padding:15px 20px;border-radius:10px;z-index:9999;box-shadow:0 10px 30px rgba(0,0,0,0.3);font-weight:600;';
      document.body.appendChild(successToast);
      setTimeout(() => document.body.removeChild(successToast), 3000);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Remove loading toast if exists
      const loadingToast = document.querySelector('div[style*="position:fixed"]');
      if (loadingToast) document.body.removeChild(loadingToast);
      
      alert(language === 'en' ? 'Error generating PDF. Please try again.' : 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  if (showResults && scores) {
    const chartData = [
      { name: language === 'en' ? 'Workflow' : 'Flux de travail', score: scores.workflow },
      { name: language === 'en' ? 'Infrastructure' : 'Infrastructure', score: scores.infrastructure },
      { name: language === 'en' ? 'Security' : 'Sécurité', score: scores.security },
    ];

    const radarData = [
      { subject: language === 'en' ? 'Digital Workflows' : 'Flux Numériques', A: scores.workflow, fullMark: 100 },
      { subject: language === 'en' ? 'Infrastructure' : 'Infrastructure', A: scores.infrastructure, fullMark: 100 },
      { subject: language === 'en' ? 'Cybersecurity' : 'Cybersécurité', A: scores.security, fullMark: 100 },
    ];


    const maturityInfo = getMaturityLevel();

    return (
      <div className="min-h-screen bg-white">
        {/* Header Section */}
        <div className="bg-white py-12 border-b border-gray-200">
          
          {/* Back to Home Button */}
          <div className="absolute top-6 left-6 z-30">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
            >
              <Home className="h-4 w-4" />
              {language === 'en' ? 'Back to Home' : 'Retour à l\'Accueil'}
            </Button>
          </div>
          
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center space-x-2 border rounded-full px-6 py-3 mb-6"
                style={{ 
                  borderColor: maturityInfo.color,
                  backgroundColor: maturityInfo.bgColor
                }}
              >
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: maturityInfo.color }}
                />
                <span 
                  className="text-sm font-medium"
                  style={{ color: maturityInfo.color }}
                >
                  {language === 'en' ? 'Assessment Complete' : 'Évaluation Terminée'}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                {language === 'en' ? 'Your Digital Transformation Report' : 'Votre Rapport de Transformation Numérique'}
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {language === 'en' 
                  ? 'Comprehensive analysis of your organization\'s digital readiness and strategic recommendations for growth.' 
                  : 'Analyse complète de la préparation numérique de votre organisation et recommandations stratégiques pour la croissance.'}
              </p>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 -mt-8 relative z-20">
        <div id="assessment-results" className="max-w-6xl mx-auto space-y-6 bg-white print:bg-white">
            
            {/* Company Info Card - Black Background with White Text */}
            <Card className="border border-gray-200 shadow-sm print:shadow-none bg-black">
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded border border-white flex items-center justify-center bg-gray-800">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2-2v-2a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-white">{form.getValues().company || 'Your Organization'}</h3>
                    </div>
                    <div className="border border-white rounded p-4 space-y-2 bg-black">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2z" />
                        </svg>
                        <p className="font-medium text-white">
                          {language === 'en' ? 'Assessment Date:' : 'Date d\'évaluation:'} 
                          <span className="text-white ml-2 font-semibold">{new Date().toLocaleDateString()}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="font-medium text-white">
                          {language === 'en' ? 'Report Generated by:' : 'Rapport généré par:'} 
                          <span className="text-white ml-2 font-semibold">QuantumBox.ai</span>
                        </p>
                      </div>
                      <div className="pt-2 border-t border-white mt-3">
                        <p className="text-lg font-bold text-white">
                          "TESTING"
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div 
                      className="inline-flex items-center justify-center w-32 h-32 rounded-full border-3"
                      style={{ 
                        borderColor: maturityInfo.color,
                        backgroundColor: maturityInfo.bgColor
                      }}
                    >
                      <div className="text-center">
                        <div 
                          className="text-3xl font-bold mb-1"
                          style={{ color: maturityInfo.color }}
                        >
                          {scores.total}%
                        </div>
                        <div 
                          className="text-sm font-semibold"
                          style={{ color: maturityInfo.color }}
                        >
                          {maturityInfo.title}
                        </div>
                        <div className="text-xs text-gray-600 font-medium mt-1">
                          {language === 'en' ? 'Digital Maturity' : 'Maturité Numérique'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Overall Score Section */}
            <Card className="border border-gray-200 shadow-sm print:shadow-none bg-white">
              <CardHeader className="text-center pb-4 border-b border-gray-100">
                <div 
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full border-3 mb-4 mx-auto"
                  style={{ 
                    borderColor: maturityInfo.color,
                    backgroundColor: maturityInfo.bgColor
                  }}
                >
                  <span 
                    className="text-2xl font-bold"
                    style={{ color: maturityInfo.color }}
                  >
                    {scores.total}%
                  </span>
                </div>
                <CardTitle 
                  className="text-2xl mb-2"
                  style={{ color: maturityInfo.color }}
                >
                  {maturityInfo.title}
                </CardTitle>
                <CardDescription className="text-lg max-w-2xl mx-auto text-gray-700">
                  {maturityInfo.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="relative z-10 bg-white">
                <div className="space-y-4">
                  <div className="relative bg-gray-100 rounded-lg p-1">
                    <div 
                      className="h-6 rounded transition-all duration-1000 ease-out flex items-center justify-end pr-3"
                      style={{ 
                        width: `${scores.total}%`,
                        backgroundColor: maturityInfo.color
                      }}
                    >
                      <span className="text-white text-sm font-bold">
                        {scores.total}%
                      </span>
                    </div>
                  </div>
                  <p className="text-center text-gray-700 font-medium">
                    {language === 'en' ? 'Overall Digital Transformation Readiness Score' : 'Score Global de Préparation à la Transformation Numérique'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Enhanced Detailed Scores */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Interactive Bar Chart */}
              <Card className="bg-gradient-to-br from-card to-card/90 shadow-xl border-primary/10">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 text-xl">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-primary to-accent flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    {language === 'en' ? 'Score Breakdown' : 'Répartition des Scores'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
                      <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)'
                        }} 
                      />
                      <Bar 
                        dataKey="score" 
                        radius={[4, 4, 0, 0]}
                        fill="url(#colorGradient)"
                      />
                      <defs>
                        <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="hsl(var(--primary))" />
                          <stop offset="100%" stopColor="hsl(var(--accent))" />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Enhanced Radar Chart */}
              <Card className="bg-gradient-to-br from-card to-card/90 shadow-xl border-accent/10">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 text-xl">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-accent to-primary flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    {language === 'en' ? 'Maturity Radar' : 'Radar de Maturité'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="hsl(var(--muted))" opacity={0.3} />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                      <Radar
                        name="Score"
                        dataKey="A"
                        stroke="hsl(var(--primary))"
                        fill="hsl(var(--primary))"
                        fillOpacity={0.3}
                        strokeWidth={2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Enhanced Recommendations */}
            <Card className="bg-gradient-to-br from-card to-card/95 shadow-2xl border-gradient-primary/20 overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary via-accent to-primary flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  {language === 'en' ? 'Strategic Recommendations' : 'Recommandations Stratégiques'}
                </CardTitle>
                <CardDescription className="text-base">
                  {language === 'en' 
                    ? 'Personalized action items based on your assessment results' 
                    : 'Actions personnalisées basées sur les résultats de votre évaluation'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 relative z-10">
                <div className="grid lg:grid-cols-3 gap-6">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 rounded-xl blur-sm group-hover:blur-none transition-all duration-300" />
                    <div className="relative p-6 bg-card/80 backdrop-blur-sm rounded-xl border border-primary/20 hover:border-primary/40 transition-all duration-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-primary to-primary/80 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-primary">
                            {language === 'en' ? 'Digital Workflows' : 'Flux Numériques'}
                          </h4>
                          <div className="text-3xl font-bold text-primary">{scores.workflow}%</div>
                        </div>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {scores.workflow < 50 
                          ? (language === 'en' ? 'Focus on integrating your core systems and establishing basic digital workflows. Start with automating simple, repetitive tasks.' : 'Concentrez-vous sur l\'intégration de vos systèmes centraux et l\'établissement de flux numériques de base. Commencez par automatiser les tâches simples et répétitives.')
                          : (language === 'en' ? 'Excellent foundation! Consider implementing AI-driven automation and advanced workflow optimization to maximize efficiency.' : 'Excellente base ! Considérez l\'implémentation d\'automatisation pilotée par IA et d\'optimisation avancée des flux pour maximiser l\'efficacité.')
                        }
                      </p>
                    </div>
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-accent/20 to-accent/10 rounded-xl blur-sm group-hover:blur-none transition-all duration-300" />
                    <div className="relative p-6 bg-card/80 backdrop-blur-sm rounded-xl border border-accent/20 hover:border-accent/40 transition-all duration-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-accent to-accent/80 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-accent">
                            {language === 'en' ? 'Infrastructure' : 'Infrastructure'}
                          </h4>
                          <div className="text-3xl font-bold text-accent">{scores.infrastructure}%</div>
                        </div>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {scores.infrastructure < 50 
                          ? (language === 'en' ? 'Modernize your infrastructure with cloud solutions, containerization, and robust backup strategies for better scalability.' : 'Modernisez votre infrastructure avec des solutions cloud, la conteneurisation et des stratégies de sauvegarde robustes pour une meilleure évolutivité.')
                          : (language === 'en' ? 'Strong infrastructure foundation! Focus on optimization, performance tuning, and advanced monitoring for peak efficiency.' : 'Base d\'infrastructure solide ! Concentrez-vous sur l\'optimisation, le réglage des performances et la surveillance avancée pour une efficacité maximale.')
                        }
                      </p>
                    </div>
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-xl blur-sm group-hover:blur-none transition-all duration-300" />
                    <div className="relative p-6 bg-card/80 backdrop-blur-sm rounded-xl border border-primary/20 hover:border-accent/40 transition-all duration-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-primary to-accent flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-bold text-lg bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                            {language === 'en' ? 'Cybersecurity' : 'Cybersécurité'}
                          </h4>
                          <div className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{scores.security}%</div>
                        </div>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {scores.security < 50 
                          ? (language === 'en' ? 'Strengthen your security posture with access controls, regular updates, and comprehensive security tools. Security should be your top priority.' : 'Renforcez votre posture de sécurité avec des contrôles d\'accès, des mises à jour régulières et des outils de sécurité complets. La sécurité devrait être votre priorité absolue.')
                          : (language === 'en' ? 'Excellent security foundation! Maintain your security excellence with continuous monitoring and advanced threat protection.' : 'Excellente base de sécurité ! Maintenez votre excellence sécuritaire avec une surveillance continue et une protection avancée contre les menaces.')
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Investment & ROI Projection */}
            {scores.roi && (
              <Card className="bg-gradient-to-br from-card to-card/95 shadow-2xl border-gradient-primary/20 overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-2xl">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-accent via-primary to-accent flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-white" />
                    </div>
                    {language === 'en' ? 'Investment & ROI Projection' : 'Investissement et Projection ROI'}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {language === 'en' 
                      ? 'Based on your company size and digital maturity level' 
                      : 'Basé sur la taille de votre entreprise et votre niveau de maturité numérique'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 relative z-10">
                  {/* Key Metrics */}
                  <div className="grid lg:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-6 rounded-xl border border-primary/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Calculator className="w-5 h-5 text-primary" />
                        <span className="text-sm font-medium text-muted-foreground">
                          {language === 'en' ? 'Total Investment' : 'Investissement Total'}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-primary">
                        ${scores.roi.totalInvestment.toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-accent/10 to-accent/5 p-6 rounded-xl border border-accent/20">
                      <div className="flex items-center gap-3 mb-2">
                        <TrendingUp className="w-5 h-5 text-accent" />
                        <span className="text-sm font-medium text-muted-foreground">
                          {language === 'en' ? 'Estimated Annual Gains' : 'Gains Annuels Estimés'}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-accent">
                        ${scores.roi.annualSavings.toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 p-6 rounded-xl border border-green-500/20">
                      <div className="flex items-center gap-3 mb-2">
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-muted-foreground">
                          {language === 'en' ? 'Break-even' : 'Seuil de Rentabilité'}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-green-500">
                        {scores.roi.breakEvenPoint} {language === 'en' ? 'months' : 'mois'}
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 p-6 rounded-xl border border-orange-500/20">
                      <div className="flex items-center gap-3 mb-2">
                        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        <span className="text-sm font-medium text-muted-foreground">
                          {language === 'en' ? '3-Year ROI' : 'ROI 3 ans'}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-orange-500">
                        {scores.roi.threeYearROI}%
                      </div>
                    </div>
                  </div>

                  {/* ROI Projection Chart */}
                  <Card className="bg-gradient-to-br from-card to-card/90 shadow-xl border-accent/10">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-3 text-xl">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-primary to-accent flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-white" />
                        </div>
                        {language === 'en' ? '3-Year Financial Projection' : 'Projection Financière 3 Ans'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={350}>
                        <AreaChart data={scores.roi.projectionData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
                          <XAxis 
                            dataKey="year" 
                            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                            axisLine={{ stroke: 'hsl(var(--muted))' }}
                          />
                          <YAxis 
                            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                            axisLine={{ stroke: 'hsl(var(--muted))' }}
                            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'hsl(var(--popover))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}
                            formatter={(value: any, name: string) => [
                              `$${Number(value).toLocaleString()}`,
                              name === 'savings' 
                                ? (language === 'en' ? 'Savings' : 'Économies')
                                : name === 'investment'
                                ? (language === 'en' ? 'Investment' : 'Investissement')
                                : (language === 'en' ? 'Cumulative' : 'Cumulé')
                            ]}
                          />
                          <Area
                            type="monotone"
                            dataKey="savings"
                            stackId="1"
                            stroke="hsl(var(--primary))"
                            fill="hsl(var(--primary))"
                            fillOpacity={0.6}
                          />
                          <Area
                            type="monotone"
                            dataKey="investment"
                            stackId="2"
                            stroke="hsl(var(--destructive))"
                            fill="hsl(var(--destructive))"
                            fillOpacity={0.6}
                          />
                          <Line
                            type="monotone"
                            dataKey="cumulative"
                            stroke="hsl(var(--accent))"
                            strokeWidth={3}
                            strokeDasharray="5 5"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  
                  {/* ROI Insights */}
                  <div className="bg-gradient-to-r from-primary/5 to-accent/5 p-6 rounded-xl border border-primary/10">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      {language === 'en' ? 'Key Investment Insights' : 'Informations Clés d\'Investissement'}
                    </h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {language === 'en' 
                          ? `Investment calculated based on ${form.getValues().companySize} employees and ${scores.maturityLevel} digital maturity`
                          : `Investissement calculé pour ${form.getValues().companySize} employés et maturité numérique ${scores.maturityLevel}`}
                      </li>
                      <li className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {language === 'en' 
                          ? 'Projections include operational efficiency gains and cost reductions'
                          : 'Les projections incluent les gains d\'efficacité opérationnelle et les réductions de coûts'}
                      </li>
                      <li className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {language === 'en' 
                          ? 'Conservative estimates - actual results often exceed projections'
                          : 'Estimations conservatrices - les résultats réels dépassent souvent les projections'}
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Enhanced Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-8">
              <Button 
                onClick={(e) => {
                  e.preventDefault();
                  console.log('PDF button clicked');
                  generatePDF();
                }} 
                size="lg" 
                className="min-w-[200px] bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-white font-semibold py-6 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer relative z-50"
                type="button"
              >
                <Download className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Download PDF Report' : 'Télécharger le Rapport PDF'}
              </Button>
              <Button variant="outline" size="lg" className="min-w-[200px] font-semibold py-6 px-8 rounded-xl border-2 border-primary/20 hover:border-primary/40 hover:bg-primary/5 transition-all duration-300" onClick={() => window.location.href = '/contact'}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2z" />
                </svg>
                {language === 'en' ? 'Schedule Free Consultation' : 'Planifier une Consultation Gratuite'}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/5 rounded-full blur-3xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-between items-center mb-6">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex items-center gap-2"
              >
                <Home className="h-4 w-4" />
                {language === 'en' ? 'Back to Home' : 'Retour à l\'Accueil'}
              </Button>
            </div>
            <h1 className="text-4xl font-bold mb-4">
              {language === 'en' 
                ? 'Digital Transformation Readiness Assessment' 
                : 'Évaluation de Préparation à la Transformation Numérique'}
            </h1>
            <p className="text-muted-foreground text-lg">
              {language === 'en'
                ? 'Get a high-level assessment of your digital maturity. We\'ll provide a deeper analysis after review.'
                : 'Obtenez une évaluation de haut niveau de votre maturité numérique. Nous fournirons une analyse plus approfondie après examen.'}
            </p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">
                {language === 'en' ? 'Progress' : 'Progression'}
              </span>
              <span className="text-sm text-muted-foreground">
                {currentSection + 1} / {sections.length}
              </span>
            </div>
            <Progress value={((currentSection + 1) / sections.length) * 100} className="h-2" />
          </div>

          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {React.createElement(sections[currentSection].icon, { className: "h-5 w-5" })}
                    {sections[currentSection].title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Personal Information Section */}
                  {currentSection === 0 && (
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Full Name' : 'Nom Complet'}
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input type="email" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Phone Number' : 'Numéro de Téléphone'}
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Company Name' : 'Nom de l\'Entreprise'}
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Company Profile Section */}
                  {currentSection === 1 && (
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="industry"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'What is your industry?' : 'Quel est votre secteur d\'activité ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'telecom', label: language === 'en' ? 'Telecom' : 'Télécommunications' },
                                  { value: 'insurance', label: language === 'en' ? 'Insurance' : 'Assurance' },
                                  { value: 'logistics', label: language === 'en' ? 'Logistics' : 'Logistique' },
                                  { value: 'energy', label: language === 'en' ? 'Energy' : 'Énergie' },
                                  { value: 'retail', label: language === 'en' ? 'Retail' : 'Commerce de détail' },
                                  { value: 'banking', label: language === 'en' ? 'Banking / Finance' : 'Banque / Finance' },
                                  { value: 'healthcare', label: language === 'en' ? 'Healthcare' : 'Santé' },
                                  { value: 'other', label: language === 'en' ? 'Other' : 'Autre' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={option.value} />
                                    <label htmlFor={option.value} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="companySize"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Company size (employees)?' : 'Taille de l\'entreprise (employés) ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: '1-10', label: '1–10' },
                                  { value: '11-50', label: '11–50' },
                                  { value: '51-200', label: '51–200' },
                                  { value: '201-1000', label: '201–1000' },
                                  { value: '1000+', label: '1000+' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`size-${option.value}`} />
                                    <label htmlFor={`size-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Your role in the organization?' : 'Votre rôle dans l\'organisation ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'ceo', label: language === 'en' ? 'CEO / Founder' : 'PDG / Fondateur' },
                                  { value: 'cto', label: 'CTO / CIO' },
                                  { value: 'coo', label: language === 'en' ? 'Operations / COO' : 'Opérations / COO' },
                                  { value: 'innovation', label: language === 'en' ? 'Innovation Manager' : 'Responsable Innovation' },
                                  { value: 'it', label: 'IT / DevOps' },
                                  { value: 'other', label: language === 'en' ? 'Other' : 'Autre' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`role-${option.value}`} />
                                    <label htmlFor={`role-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Digital Systems Section */}
                  {currentSection === 2 && (
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="coreSystems"
                        render={() => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Which core systems do you currently use? (Select all that apply)' : 'Quels systèmes centraux utilisez-vous actuellement ? (Sélectionnez tout ce qui s\'applique)'}
                            </FormLabel>
                            <div className="grid grid-cols-2 gap-3">
                              {[
                                { value: 'erp', label: 'ERP' },
                                { value: 'crm', label: 'CRM' },
                                { value: 'email', label: language === 'en' ? 'Email (Outlook, Gmail)' : 'Email (Outlook, Gmail)' },
                                { value: 'excel', label: 'Excel / Google Sheets' },
                                { value: 'paper', label: language === 'en' ? 'Paper-based systems' : 'Systèmes papier' },
                                { value: 'custom', label: language === 'en' ? 'Custom-built software' : 'Logiciel sur mesure' },
                                { value: 'none', label: language === 'en' ? 'None' : 'Aucun' },
                              ].map((option) => (
                                <FormField
                                  key={option.value}
                                  control={form.control}
                                  name="coreSystems"
                                  render={({ field }) => (
                                    <FormItem key={option.value} className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(option.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, option.value])
                                              : field.onChange(field.value?.filter((value) => value !== option.value))
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal">
                                        {option.label}
                                      </FormLabel>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="systemsIntegration"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Are your systems integrated (data flows between them automatically)?' : 'Vos systèmes sont-ils intégrés (les données circulent automatiquement entre eux) ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'fully', label: language === 'en' ? 'Yes, fully integrated' : 'Oui, entièrement intégrés' },
                                  { value: 'partially', label: language === 'en' ? 'Partially integrated' : 'Partiellement intégrés' },
                                  { value: 'no', label: language === 'en' ? 'No integration' : 'Aucune intégration' },
                                  { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`integration-${option.value}`} />
                                    <label htmlFor={`integration-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="documentWorkflow"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'How do you currently handle document workflows (e.g. contracts, invoices)?' : 'Comment gérez-vous actuellement les flux de documents (ex: contrats, factures) ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'fully-digital', label: language === 'en' ? 'Fully digital with automation' : 'Entièrement numérique avec automatisation' },
                                  { value: 'partly-digital', label: language === 'en' ? 'Partly digital (email + manual)' : 'Partiellement numérique (email + manuel)' },
                                  { value: 'manual', label: language === 'en' ? 'Manual/paper-based' : 'Manuel/papier' },
                                  { value: 'outsourced', label: language === 'en' ? 'Outsourced' : 'Externalisé' },
                                  { value: 'no-process', label: language === 'en' ? 'No formal process' : 'Aucun processus formel' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`workflow-${option.value}`} />
                                    <label htmlFor={`workflow-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="communicationTools"
                        render={() => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'What tools do you use for communication & collaboration?' : 'Quels outils utilisez-vous pour la communication et la collaboration ?'}
                            </FormLabel>
                            <div className="grid grid-cols-2 gap-3">
                              {[
                                { value: 'teams', label: 'Microsoft Teams' },
                                { value: 'slack', label: 'Slack' },
                                { value: 'whatsapp', label: 'WhatsApp' },
                                { value: 'zoom', label: 'Zoom' },
                                { value: 'email-only', label: language === 'en' ? 'Email only' : 'Email seulement' },
                                { value: 'none', label: language === 'en' ? 'None / Other' : 'Aucun / Autre' },
                              ].map((option) => (
                                <FormField
                                  key={option.value}
                                  control={form.control}
                                  name="communicationTools"
                                  render={({ field }) => (
                                    <FormItem key={option.value} className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(option.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, option.value])
                                              : field.onChange(field.value?.filter((value) => value !== option.value))
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal">
                                        {option.label}
                                      </FormLabel>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Automation & AI Section */}
                  {currentSection === 3 && (
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="automatedProcesses"
                        render={() => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Which of these processes are automated today? (Select all that apply)' : 'Lesquels de ces processus sont automatisés aujourd\'hui ? (Sélectionnez tout ce qui s\'applique)'}
                            </FormLabel>
                            <div className="grid grid-cols-1 gap-3">
                              {[
                                { value: 'customer-service', label: language === 'en' ? 'Customer service (chatbots, IVR)' : 'Service client (chatbots, IVR)' },
                                { value: 'hr-onboarding', label: language === 'en' ? 'HR onboarding' : 'Intégration RH' },
                                { value: 'invoicing', label: language === 'en' ? 'Invoicing / billing' : 'Facturation' },
                                { value: 'inventory', label: language === 'en' ? 'Inventory tracking' : 'Suivi des stocks' },
                                { value: 'data-entry', label: language === 'en' ? 'Data entry / reporting' : 'Saisie de données / rapports' },
                                { value: 'none', label: language === 'en' ? 'None of the above' : 'Aucun des éléments ci-dessus' },
                              ].map((option) => (
                                <FormField
                                  key={option.value}
                                  control={form.control}
                                  name="automatedProcesses"
                                  render={({ field }) => (
                                    <FormItem key={option.value} className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(option.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, option.value])
                                              : field.onChange(field.value?.filter((value) => value !== option.value))
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal">
                                        {option.label}
                                      </FormLabel>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="aiUsage"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Are you currently using any AI or machine learning tools?' : 'Utilisez-vous actuellement des outils d\'IA ou d\'apprentissage automatique ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'yes-actively', label: language === 'en' ? 'Yes, actively' : 'Oui, activement' },
                                  { value: 'exploring', label: language === 'en' ? 'We\'re exploring options' : 'Nous explorons les options' },
                                  { value: 'interested', label: language === 'en' ? 'No, but interested' : 'Non, mais intéressé' },
                                  { value: 'not-interested', label: language === 'en' ? 'No, and not interested' : 'Non, et pas intéressé' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`ai-${option.value}`} />
                                    <label htmlFor={`ai-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="painPoint"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'What is your biggest operational pain point today?' : 'Quel est votre plus grand point de douleur opérationnel aujourd\'hui ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'manual-tasks', label: language === 'en' ? 'Time-consuming manual tasks' : 'Tâches manuelles chronophages' },
                                  { value: 'disconnected-tools', label: language === 'en' ? 'Disconnected tools / systems' : 'Outils / systèmes déconnectés' },
                                  { value: 'data-visibility', label: language === 'en' ? 'Lack of data visibility' : 'Manque de visibilité des données' },
                                  { value: 'customer-service', label: language === 'en' ? 'Poor customer service response time' : 'Temps de réponse du service client faible' },
                                  { value: 'scaling', label: language === 'en' ? 'Difficulty scaling operations' : 'Difficulté à faire évoluer les opérations' },
                                  { value: 'other', label: language === 'en' ? 'Other' : 'Autre' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`pain-${option.value}`} />
                                    <label htmlFor={`pain-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="digitalStrategy"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Do you have a documented digital transformation strategy or roadmap?' : 'Avez-vous une stratégie ou une feuille de route de transformation numérique documentée ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'fully-developed', label: language === 'en' ? 'Yes, fully developed' : 'Oui, entièrement développée' },
                                  { value: 'in-progress', label: language === 'en' ? 'In progress' : 'En cours' },
                                  { value: 'planned', label: language === 'en' ? 'No, but planned' : 'Non, mais planifié' },
                                  { value: 'no', label: language === 'en' ? 'No' : 'Non' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`strategy-${option.value}`} />
                                    <label htmlFor={`strategy-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="techAdoption"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'How often do you evaluate or adopt new tech tools?' : 'À quelle fréquence évaluez-vous ou adoptez-vous de nouveaux outils technologiques ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'quarterly', label: language === 'en' ? 'Quarterly' : 'Trimestriellement' },
                                  { value: 'yearly', label: language === 'en' ? 'Once a year' : 'Une fois par an' },
                                  { value: 'rarely', label: language === 'en' ? 'Rarely' : 'Rarement' },
                                  { value: 'never', label: language === 'en' ? 'Never' : 'Jamais' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`adoption-${option.value}`} />
                                    <label htmlFor={`adoption-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="teamOpenness"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'How open is your team to adopting new tools and digital workflows?' : 'Dans quelle mesure votre équipe est-elle ouverte à l\'adoption de nouveaux outils et flux de travail numériques ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'very-open', label: language === 'en' ? 'Very open' : 'Très ouvert' },
                                  { value: 'some-resistance', label: language === 'en' ? 'Some resistance' : 'Quelques résistances' },
                                  { value: 'resistant', label: language === 'en' ? 'Resistant / skeptical' : 'Résistant / sceptique' },
                                  { value: 'dont-know', label: language === 'en' ? 'I don\'t know' : 'Je ne sais pas' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`openness-${option.value}`} />
                                    <label htmlFor={`openness-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Infrastructure Section */}
                  {currentSection === 4 && (
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="hosting"
                        render={() => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Where is your infrastructure currently hosted? (Select all that apply)' : 'Où votre infrastructure est-elle actuellement hébergée ? (Sélectionnez tout ce qui s\'applique)'}
                            </FormLabel>
                            <div className="grid grid-cols-1 gap-3">
                              {[
                                { value: 'on-premise', label: language === 'en' ? 'On-premise servers' : 'Serveurs sur site' },
                                { value: 'vm', label: language === 'en' ? 'Virtual machines (VMware, Hyper-V)' : 'Machines virtuelles (VMware, Hyper-V)' },
                                { value: 'private-cloud', label: language === 'en' ? 'Private cloud (e.g. OpenStack)' : 'Cloud privé (ex: OpenStack)' },
                                { value: 'public-cloud', label: language === 'en' ? 'Public cloud (AWS, Azure, GCP)' : 'Cloud public (AWS, Azure, GCP)' },
                                { value: 'hybrid', label: language === 'en' ? 'Hybrid environment' : 'Environnement hybride' },
                                { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                              ].map((option) => (
                                <FormField
                                  key={option.value}
                                  control={form.control}
                                  name="hosting"
                                  render={({ field }) => (
                                    <FormItem key={option.value} className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(option.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, option.value])
                                              : field.onChange(field.value?.filter((value) => value !== option.value))
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal">
                                        {option.label}
                                      </FormLabel>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="operatingSystems"
                        render={() => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'What operating systems are in use? (Select all that apply)' : 'Quels systèmes d\'exploitation sont utilisés ? (Sélectionnez tout ce qui s\'applique)'}
                            </FormLabel>
                            <div className="grid grid-cols-2 gap-3">
                              {[
                                { value: 'windows', label: 'Windows Server' },
                                { value: 'linux', label: 'Linux (Ubuntu, CentOS, etc.)' },
                                { value: 'macos', label: 'MacOS' },
                                { value: 'other', label: language === 'en' ? 'Other / Not sure' : 'Autre / Pas sûr' },
                              ].map((option) => (
                                <FormField
                                  key={option.value}
                                  control={form.control}
                                  name="operatingSystems"
                                  render={({ field }) => (
                                    <FormItem key={option.value} className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(option.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, option.value])
                                              : field.onChange(field.value?.filter((value) => value !== option.value))
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal">
                                        {option.label}
                                      </FormLabel>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="containers"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Do you use containers or orchestration tools like Docker/Kubernetes?' : 'Utilisez-vous des conteneurs ou des outils d\'orchestration comme Docker/Kubernetes ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'yes', label: language === 'en' ? 'Yes' : 'Oui' },
                                  { value: 'planning', label: language === 'en' ? 'Planning to' : 'Prévu' },
                                  { value: 'no', label: language === 'en' ? 'No' : 'Non' },
                                  { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`containers-${option.value}`} />
                                    <label htmlFor={`containers-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="databases"
                        render={() => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'What type of databases are used in your organization? (Select all that apply)' : 'Quels types de bases de données sont utilisés dans votre organisation ? (Sélectionnez tout ce qui s\'applique)'}
                            </FormLabel>
                            <div className="grid grid-cols-1 gap-3">
                              {[
                                { value: 'sql', label: 'SQL (MySQL, PostgreSQL, MSSQL)' },
                                { value: 'nosql', label: 'NoSQL (MongoDB, Cassandra, etc.)' },
                                { value: 'cloud-native', label: language === 'en' ? 'Cloud-native (BigQuery, DynamoDB)' : 'Cloud natif (BigQuery, DynamoDB)' },
                                { value: 'flat-files', label: language === 'en' ? 'Flat files (Excel, CSV, etc.)' : 'Fichiers plats (Excel, CSV, etc.)' },
                                { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                              ].map((option) => (
                                <FormField
                                  key={option.value}
                                  control={form.control}
                                  name="databases"
                                  render={({ field }) => (
                                    <FormItem key={option.value} className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(option.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, option.value])
                                              : field.onChange(field.value?.filter((value) => value !== option.value))
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal">
                                        {option.label}
                                      </FormLabel>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="backupStrategy"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'How is your data backed up?' : 'Comment vos données sont-elles sauvegardées ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'cloud-automated', label: language === 'en' ? 'Cloud-based, automated' : 'Cloud, automatisé' },
                                  { value: 'local-offline', label: language === 'en' ? 'Local/offline backups' : 'Sauvegardes locales/hors ligne' },
                                  { value: 'no-strategy', label: language === 'en' ? 'No backup strategy in place' : 'Aucune stratégie de sauvegarde en place' },
                                  { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`backup-${option.value}`} />
                                    <label htmlFor={`backup-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Security Section */}
                  {currentSection === 5 && (
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="accessControl"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Do you have user access control and role-based permissions in place?' : 'Avez-vous un contrôle d\'accès utilisateur et des permissions basées sur les rôles en place ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'strict', label: language === 'en' ? 'Yes, strictly enforced' : 'Oui, strictement appliqué' },
                                  { value: 'partially', label: language === 'en' ? 'Partially' : 'Partiellement' },
                                  { value: 'no', label: language === 'en' ? 'No' : 'Non' },
                                  { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`access-${option.value}`} />
                                    <label htmlFor={`access-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="securityTools"
                        render={() => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'What cybersecurity tools do you have implemented? (Select all that apply)' : 'Quels outils de cybersécurité avez-vous mis en place ? (Sélectionnez tout ce qui s\'applique)'}
                            </FormLabel>
                            <div className="grid grid-cols-1 gap-3">
                              {[
                                { value: 'antivirus', label: language === 'en' ? 'Antivirus / Endpoint security' : 'Antivirus / Sécurité des points de terminaison' },
                                { value: 'firewall', label: 'Firewall' },
                                { value: 'vpn', label: 'VPN' },
                                { value: 'ids-ips', label: 'IDS / IPS' },
                                { value: 'none', label: language === 'en' ? 'None' : 'Aucun' },
                                { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                              ].map((option) => (
                                <FormField
                                  key={option.value}
                                  control={form.control}
                                  name="securityTools"
                                  render={({ field }) => (
                                    <FormItem key={option.value} className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(option.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, option.value])
                                              : field.onChange(field.value?.filter((value) => value !== option.value))
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal">
                                        {option.label}
                                      </FormLabel>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="systemUpdates"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'How often are your systems patched and updated?' : 'À quelle fréquence vos systèmes sont-ils corrigés et mis à jour ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'automatic', label: language === 'en' ? 'Automatically / regularly' : 'Automatiquement / régulièrement' },
                                  { value: 'occasionally', label: language === 'en' ? 'Occasionally' : 'Occasionnellement' },
                                  { value: 'rarely', label: language === 'en' ? 'Rarely' : 'Rarement' },
                                  { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`updates-${option.value}`} />
                                    <label htmlFor={`updates-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="remoteAccess"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Do remote employees access internal systems securely (VPN or Zero Trust)?' : 'Les employés distants accèdent-ils aux systèmes internes de manière sécurisée (VPN ou Zero Trust) ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'yes', label: language === 'en' ? 'Yes' : 'Oui' },
                                  { value: 'no', label: language === 'en' ? 'No' : 'Non' },
                                  { value: 'not-applicable', label: language === 'en' ? 'Not applicable' : 'Non applicable' },
                                  { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`remote-${option.value}`} />
                                    <label htmlFor={`remote-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="itTeam"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Do you have a centralized IT/network team managing infrastructure?' : 'Avez-vous une équipe IT/réseau centralisée gérant l\'infrastructure ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'in-house', label: language === 'en' ? 'Yes, fully in-house' : 'Oui, entièrement interne' },
                                  { value: 'partially-outsourced', label: language === 'en' ? 'Partially outsourced' : 'Partiellement externalisé' },
                                  { value: 'ad-hoc', label: language === 'en' ? 'No / ad hoc' : 'Non / ad hoc' },
                                  { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`it-${option.value}`} />
                                    <label htmlFor={`it-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Business Goals Section */}
                  {currentSection === 6 && (
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="priorities"
                        render={() => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Which of the following are your current digital priorities? (Select up to 3)' : 'Lesquelles des priorités suivantes sont vos priorités numériques actuelles ? (Sélectionnez jusqu\'à 3)'}
                            </FormLabel>
                            <div className="grid grid-cols-1 gap-3">
                              {[
                                { value: 'automate-workflows', label: language === 'en' ? 'Automate manual workflows' : 'Automatiser les flux de travail manuels' },
                                { value: 'improve-customer-service', label: language === 'en' ? 'Improve customer service' : 'Améliorer le service client' },
                                { value: 'modernize-infrastructure', label: language === 'en' ? 'Modernize IT infrastructure' : 'Moderniser l\'infrastructure IT' },
                                { value: 'increase-productivity', label: language === 'en' ? 'Increase internal productivity' : 'Augmenter la productivité interne' },
                                { value: 'improve-security', label: language === 'en' ? 'Improve security and compliance' : 'Améliorer la sécurité et la conformité' },
                                { value: 'expand-digital-channels', label: language === 'en' ? 'Expand to new digital channels' : 'Étendre vers de nouveaux canaux numériques' },
                                { value: 'implement-ai', label: language === 'en' ? 'Implement AI-driven tools' : 'Implémenter des outils basés sur l\'IA' },
                                { value: 'not-sure', label: language === 'en' ? 'Not sure' : 'Pas sûr' },
                              ].map((option) => (
                                <FormField
                                  key={option.value}
                                  control={form.control}
                                  name="priorities"
                                  render={({ field }) => (
                                    <FormItem key={option.value} className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(option.value)}
                                          onCheckedChange={(checked) => {
                                            const currentValues = field.value || [];
                                            if (checked) {
                                              if (currentValues.length < 3) {
                                                field.onChange([...currentValues, option.value]);
                                              }
                                            } else {
                                              field.onChange(currentValues.filter((value) => value !== option.value));
                                            }
                                          }}
                                          disabled={!field.value?.includes(option.value) && field.value?.length >= 3}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal">
                                        {option.label}
                                      </FormLabel>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="wantCall"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Would you like a free call to discuss your personalized results?' : 'Souhaitez-vous un appel gratuit pour discuter de vos résultats personnalisés ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'yes', label: language === 'en' ? 'Yes' : 'Oui' },
                                  { value: 'no', label: language === 'en' ? 'No' : 'Non' },
                                  { value: 'maybe', label: language === 'en' ? 'Maybe later' : 'Peut-être plus tard' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`call-${option.value}`} />
                                    <label htmlFor={`call-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Final section */}
                  {currentSection === 6 && (
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="wantCall"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === 'en' ? 'Would you like a free call to discuss your personalized results?' : 'Souhaitez-vous un appel gratuit pour discuter de vos résultats personnalisés ?'}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup onValueChange={field.onChange} value={field.value}>
                                {[
                                  { value: 'yes', label: language === 'en' ? 'Yes' : 'Oui' },
                                  { value: 'no', label: language === 'en' ? 'No' : 'Non' },
                                  { value: 'maybe', label: language === 'en' ? 'Maybe later' : 'Peut-être plus tard' },
                                ].map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`call-${option.value}`} />
                                    <label htmlFor={`call-${option.value}`} className="text-sm font-medium">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Navigation */}
              <div className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevSection}
                  disabled={currentSection === 0}
                >
                  {language === 'en' ? 'Previous' : 'Précédent'}
                </Button>
                
                {currentSection === sections.length - 1 ? (
                  <Button type="submit">
                    {language === 'en' ? 'Get Results' : 'Obtenir les Résultats'}
                  </Button>
                ) : (
                  <Button type="button" onClick={nextSection}>
                    {language === 'en' ? 'Next' : 'Suivant'}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Assessment;